  import React, { useState, useRef, useEffect } from "react";
  import { Toast } from "primereact/toast";
  import { Menu } from "primereact/menu";
  import { Button } from "primereact/button";
  import ReusableTable from "../../dashboard/ReusableTable";
  import ReusableForm from "../../common/ReusableForm";
  import ReusableDeleteDialog from '../../common/ReusableDeleteDialog';
  import { getRequest, postRequest } from "../../services/apiService"; // Updated imports

  const ActionCell = ({ rowData, onEdit, onDelete }) => {
    const menuRef = useRef(null);
    const menuItems = [
      {
        label: "Edit",
        icon: "pi pi-pencil",
        command: () => {
          onEdit(rowData);
        }
      },
      {
        label: "Delete",
        icon: "pi pi-trash",
        command: () => {
          onDelete(rowData);
        }
      }
    ];

    return (
      <div className="flex justify-center">
        <Button
          icon="pi pi-ellipsis-h"
          className="p-button-rounded p-button-text"
          onClick={(e) => menuRef.current.toggle(e)}
        />
        <Menu ref={menuRef} model={menuItems} popup />
      </div>
    );
  };

  export default function MilestoneManagement() {
    const [milestones, setMilestones] = useState([]);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [selectedMilestone, setSelectedMilestone] = useState(null);
    const [newMilestone, setNewMilestone] = useState({
      id: null,
      title: "",
      milestone_type: "",
      dependencies: []
    });
    const [loading, setLoading] = useState(false);
    const toast = useRef(null);

    // Pagination state
    const [totalRecords, setTotalRecords] = useState(0);
    const [lazyState, setLazyState] = useState({
      first: 0,
      rows: 10,
      page: 1,
    });
    const [search, setSearch] = useState("");
    const searchTimeout = useRef(null);

    const [allMilestones, setAllMilestones] = useState([]);

    const API_BASE = 'https://pms-api.ilizien-projects-cdf.in/master/milestones/';

    // Fetch all milestones for dependencies dropdown
    useEffect(() => {
      getRequest(`/master/milestones/?search=${encodeURIComponent(search)}&page=${lazyState.page}`)
        .then(response => {
          if (response && response.results?.data) {
            setAllMilestones(response.results.data);
          }
        })
        .catch(error => {
          console.error("Error fetching all milestones for dependencies:", error);
        });
    }, []);

    const milestoneFormFields = [
      {
        name: "title",
        label: "Milestone Title",
        type: "text",
        required: true,
        placeholder: "Enter milestone title"
      },
      {
        name: "milestone_type",
        label: "Milestone Type",
        type: "select",
        required: true,
        options: [
          { label: "Core", value: "core" },
          { label: "Payment", value: "payment" }
        ],
        placeholder: "Select milestone type"
      },
      {
        name: "dependencies",
        label: "Dependencies",
        type: "multiselect",
        required: false,
        options: allMilestones
          .filter(m => m.id !== newMilestone.id)
          .map(m => ({
            label: m.title,
            value: m.id
          })),
        placeholder: "Select dependent milestones",
        helpText: "Select milestones that this milestone depends on",
        width: "col-span-full"
      }
    ];

    const deleteFormFields = [
      {
        name: "confirmation",
        label: "Confirm Deletion",
        type: "text",
        required: true,
        placeholder: "Type 'DELETE' to confirm",
        helpText: "This action cannot be undone. Type 'DELETE' to confirm deletion."
      }
    ];

    const [deleteFormData, setDeleteFormData] = useState({
      confirmation: ""
    });

    // Debounced search effect for table search
    useEffect(() => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
      searchTimeout.current = setTimeout(() => {
        setLoading(true);
        // Always send 1-based page number to API
        const apiPage = Math.floor(lazyState.first / lazyState.rows) + 1;
        getRequest(`/master/milestones/?search=${encodeURIComponent(search)}&page=${apiPage}`)
          .then(response => {
            if (response && response.results?.data) {
              setMilestones(response.results.data || []);
              setTotalRecords(response.count || 0);
              setLazyState(prev => ({
                ...prev,
                page: apiPage,
              }));
            } else {
              setMilestones([]);
              setTotalRecords(0);
              toast.current.show({
                severity: "error",
                summary: "Error",
                detail: "No data available from the server",
                life: 3000
              });
            }
          })
          .catch(error => {
            console.error("Error fetching milestones:", error);
            setMilestones([]);
            setTotalRecords(0);
            toast.current.show({
              severity: "error",
              summary: "Error",
              detail: "Failed to fetch milestones. Please try again later.",
              life: 3000
            });
          })
          .finally(() => setLoading(false));
      }, 700); // 700ms debounce
      return () => {
        if (searchTimeout.current) {
          clearTimeout(searchTimeout.current);
        }
      };
    }, [search, lazyState.first, lazyState.rows]);

    const handleTableSearch = (value) => {
      setSearch(value);
    };

    const onPageChange = (event) => {
      // event.first is 0-based index of first record, event.rows is page size
      // Calculate 1-based page number for API
      const newPage = Math.floor(event.first / event.rows) + 1;
      setLazyState({
        ...lazyState,
        first: event.first,
        rows: event.rows,
        page: newPage,
      });
    };

    const openEditModal = (milestone) => {
    setNewMilestone({
      id: milestone.id,
      title: milestone.title || "",
      milestone_type: milestone.milestone_type || "",
      // Correctly map dependencies to an array of their IDs
      dependencies: milestone.dependent_on?.map(dep => dep.id) || []
    });
    setIsEditModalOpen(true);
  };

    const openDeleteDialog = (milestone) => {
      setSelectedMilestone(milestone);
      setDeleteFormData({ confirmation: "" });
      setIsDeleteDialogOpen(true);
    };

    const openAddDialog = () => {
      setNewMilestone({
        id: null,
        title: "",
        milestone_type: "",
        dependencies: []
      });
      setIsEditModalOpen(true);
    };

    const handleSubmit = async () => {
      if (!newMilestone.title || !newMilestone.milestone_type) {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Title and milestone type are required",
          life: 3000
        });
        return;
      }
      
      try {
        // Common payload structure for both create and update
        const payload = {
          title: newMilestone.title,
          milestone_type: newMilestone.milestone_type,
          dependencies: newMilestone.dependencies || []
        };

        // For update operations, include ID and status_db=1
        if (newMilestone.id) {
          payload.id = newMilestone.id;
          payload.status_db = 1; // Indicates update operation
          
          await postRequest(API_BASE, payload);
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: "Milestone updated successfully",
            life: 3000
          });
        } else {
          // For create operations
          await postRequest(API_BASE, payload);
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: "Milestone added successfully",
            life: 3000
          });
        }
        setLazyState(prev => ({ ...prev })); // Trigger refresh via useEffect
        setIsEditModalOpen(false);
      } catch (error) {
        console.error("Error saving milestone:", error);
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to save milestone",
          life: 3000
        });
      }
    };

    const confirmDelete = async () => {
      if (deleteFormData.confirmation !== "DELETE") {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Please type 'DELETE' to confirm",
          life: 3000
        });
        return;
      }

      try {
        // Use POST with status_db=3 for delete operation
        await postRequest(API_BASE, {
          id: selectedMilestone.id,
          status_db: 3  // Delete indicator
        });
        
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Milestone deleted successfully",
          life: 3000
        });
        setLazyState(prev => ({ ...prev })); // Trigger refresh via useEffect
        setIsDeleteDialogOpen(false);
        setSelectedMilestone(null);
        setDeleteFormData({ confirmation: "" });
      } catch (error) {
        console.error("Error deleting milestone:", error);
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to delete milestone",
          life: 3000
        });
      }
    };

    const columns = [
    {
      field: "actions",
      header: "Actions",
      body: (rowData) => (
        <ActionCell
          rowData={rowData}
          onEdit={openEditModal}
          onDelete={openDeleteDialog}
        />
      ),
      sortable: false
    },
    {
      field: "title",
      header: "Title",
      sortable: true
    },
    {
      field: "milestone_type",
      header: "Type",
      sortable: true,
      body: (rowData) => rowData.milestone_type ? rowData.milestone_type.charAt(0).toUpperCase() + rowData.milestone_type.slice(1) : '-'
    },
    {
      field: "dependent_on",
      header: "Dependencies",
      sortable: false,
      body: (rowData) => (
        <div>
          {rowData.dependent_on && rowData.dependent_on.length > 0
            ? rowData.dependent_on.map(dep => dep.title).join(", ")
            : '-'}
        </div>
      )
    }
  ];

    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <Toast ref={toast} position="top-right" />
        <div className="max-w-full mx-auto">
          <ReusableTable
            title="Milestone Management"
            data={milestones}
            columnFields={columns}
            rows={lazyState.rows}
            colorScheme={{
              headerBgColor: '#1e3a8a',
              headerTextColor: '#ffffff',
              tableBgColor: '#ffffff',
              tableTextColor: '#334155',
              buttonBgColor: '#3b82f6',
              buttonTextColor: '#ffffff',
              deleteButtonColor: '#ef4444',
              addButtonColor: '#10b981'
            }}
            setAddNewDialogVisible={openAddDialog}
            totalRecords={totalRecords}
            onPageChange={onPageChange}
            first={lazyState.first}
            loading={loading}
            searchValue={search}
            onSearchChange={handleTableSearch}
          />

          {/* Edit/Add Milestone Modal with explicit wrapper */}
          {isEditModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
              <div className="bg-white rounded-xl shadow-2xl w-full mx-auto max-w-2xl animate-fade-in"> {/* Increased width for 3 fields in a row */}
                <ReusableForm
                  headerText={newMilestone.id ? "Edit Milestone" : "Add New Milestone"}
                  formData={newMilestone}
                  setFormData={setNewMilestone}
                  fields={milestoneFormFields}
                  onSubmit={handleSubmit}
                  onHide={() => setIsEditModalOpen(false)}
                  loading={loading} // Pass loading state if ReusableForm handles it
                />
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          <ReusableDeleteDialog
            visible={isDeleteDialogOpen}
            onHide={() => setIsDeleteDialogOpen(false)}
            onDelete={confirmDelete}
            fields={deleteFormFields}
            formData={deleteFormData}
            setFormData={setDeleteFormData}
            loading={loading}
            title="Delete Milestone"
            description={`Are you sure you want to delete milestone "${selectedMilestone?.title}"? This action cannot be undone. Type 'DELETE' to confirm.`}
            deleteLabel="Delete"
          />
        </div>
      </div>
    );
  }